import { Link } from "react-router-dom";
import { buildItemLink } from "../utils/canvas.js";
import { ArrowLongRightIcon, CheckIcon, DocumentTextIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import type { TrpcOutput } from "../utils/trpc.js";

type Module = NonNullable<TrpcOutput["course"]["getModules"]>[number];
type ModuleItem = Module["subModules"][number]["items"][number];

interface ModuleContentProps {
  module: Module;
  completedByItemId: Set<string>;
  isProgressionSuccess: boolean;
  hasRequirements: boolean;
  hasStarted: boolean;
  globalNextRequiredItem: ModuleItem | null;
}

// Renders a module's sub-modules and items; shared by the stacked and sidebar journey layouts.
export default function ModuleContent({
  module,
  completedByItemId,
  isProgressionSuccess,
  hasRequirements,
  hasStarted,
  globalNextRequiredItem,
}: ModuleContentProps) {
  return (
    <div className="relative">
      <div className="space-y-6">
        {(module.subModules ?? []).map((sub, subIndex) => {
          const isLastSubModule = subIndex === (module.subModules?.length ?? 0) - 1;
          const requiredSubItems = sub.items.filter(item => item.completion_requirement);
          const completedSubCount = requiredSubItems.filter(item => completedByItemId.has(String(item.id))).length;
          const isSubComplete = completedSubCount === requiredSubItems.length;

          return (
            <div key={sub.id} className="flex gap-4">
              {hasRequirements && isProgressionSuccess ? (
                <div className="relative flex w-6 justify-center">
                  <div
                    className={`absolute left-1/2 top-1 h-full w-px -translate-x-1/2 bg-gray`}
                    style={{ height: isLastSubModule ? 0 : "calc(100% + 24px)" }}
                  />
                  <span
                    className={`relative z-10 mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors duration-300 ${
                      isSubComplete
                        ? requiredSubItems.length === 0
                          ? "border-gray bg-white"
                          : "border-lavaux bg-lavaux text-white"
                        : "border-gray bg-white text-[0]"
                    } `}>
                    {isSubComplete ? <CheckIcon className="h-3 w-3" /> : null}
                  </span>
                </div>
              ) : null}

              <div className="min-w-0 flex-1">
                {sub.title ? <div className="font-semibold">{sub.title}</div> : null}
                <ul className="mt-3 divide-y divide-gray overflow-hidden rounded-2xl border border-gray bg-white/40">
                  {sub.items.map(item => {
                    const to = buildItemLink(item);
                    const isCompleted = completedByItemId.has(String(item.id));
                    const showCompletion = Boolean(item.completion_requirement);
                    const isOptional = sub.required === false;
                    const isNextItem =
                      isProgressionSuccess &&
                      globalNextRequiredItem &&
                      String(item.id) === String(globalNextRequiredItem.id);
                    const ctaLink = isNextItem && to ? to : null;

                    return (
                      <li key={item.id}>
                        <Link
                          to={to ?? "#"}
                          className="group flex items-center justify-between gap-4 rounded-2xl px-4 py-3 transition hover:bg-lac-leman-8/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lac-leman focus-visible:ring-offset-2 focus-visible:ring-offset-white">
                          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                            <span className="inline-flex min-w-0 items-center text-sm font-medium">
                              {item.type === "Page" && (
                                <DocumentTextIcon className="mr-2 h-5 w-5 shrink-0 text-lac-leman" />
                              )}
                              {item.type === "Assignment" && (
                                <PencilSquareIcon className="mr-2 h-5 w-5 shrink-0 text-lac-leman" />
                              )}
                              {item.title}
                            </span>
                            {isOptional ? (
                              <span className="text-gray-500 ml-auto inline-flex shrink-0 items-center rounded-full border border-gray px-2 py-1 text-[9px] font-semibold uppercase leading-none tracking-[0.15em]">
                                Optional
                              </span>
                            ) : null}
                            {ctaLink ? (
                              <span className="ml-6 inline-flex shrink-0 items-center rounded-full bg-lac-leman px-3 py-1 text-white">
                                <span className="flex items-center gap-2">
                                  {hasStarted ? "Continue" : "Start"}
                                  <ArrowLongRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                </span>
                              </span>
                            ) : null}
                          </div>

                          {showCompletion && isCompleted && (
                            <div className="flex items-center gap-3">
                              <span
                                className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                                  isCompleted ? "border-lavaux bg-lavaux text-white" : "hidden"
                                }`}>
                                {isCompleted ? <CheckIcon className="h-3 w-3" /> : null}
                              </span>
                            </div>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
